import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { combineLatest } from 'rxjs';
import { filter, map, mergeMap, take } from 'rxjs/operators';
import { environment } from '@env/environment';
import { AppTitleService } from '@core/services/app-title.service';
import { PrimeNG } from 'primeng/config';
import { FilterRegistrationService } from '@core/services/filter-registration.service';
import { RelativityDatasourceService } from '@core/services/relativity-datasource.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  standalone: false,
})
@UntilDestroy()
export class AppComponent implements OnInit, OnDestroy {
  constructor(
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private readonly titleService: AppTitleService,
    private readonly primengConfig: PrimeNG,
    private readonly filterRegistrationService: FilterRegistrationService,
    private readonly relativityDatasourceService: RelativityDatasourceService
  ) {}

  ngOnInit(): void {
    this.primengConfig.ripple.set(true);

    this.activatedRoute.queryParams
      .pipe(untilDestroyed(this))
      .subscribe((queryParams) => {
        const ds = queryParams['relativity_datasource'];
        this.relativityDatasourceService.setDatasource(ds || null);
      });

    combineLatest([
      this.router.events.pipe(
        filter((event) => event instanceof NavigationEnd),
        map((event) => ({ route: this.activatedRoute, event })),
        filter(({ route }) => route.outlet === 'primary'),
        map((value) => {
          while (value.route.firstChild) {
            value.route = value.route.firstChild;
          }
          while (
            value.route.snapshot.data['title'] === undefined &&
            value.route.parent
          ) {
            value.route = value.route.parent;
          }
          return value;
        }),
        mergeMap(({ route, event }) =>
          combineLatest([route.data, route.queryParams]).pipe(
            take(1),
            map((results) => ({
              data: results[0],
              queryParams: results[1],
              event,
            }))
          )
        )
      ),
    ]).subscribe(([route]) => {
      if (route.data.title) {
        let title = '';
        if (route.data.title instanceof Function) {
          title = route.data.title(route.data);
        } else {
          title = route.data.title;
        }

        if (title) {
          this.titleService.set({
            title,
            prefix: route.data.titleSuffix,
            browserTitle: route.data.browserTitle,
          });
        }
      }
    });

    if (environment.plausible) {
      this.createPlausibleScript(environment.plausible);
    }

    this.filterRegistrationService.registerFilters();
  }

  createPlausibleScript(domain: string) {
    const script = document.createElement('script');
    script.setAttribute('data-domain', domain);
    script.defer = true;
    script.src = 'https://plausible.io/js/plausible.js';
    const body = document.getElementsByTagName('body')[0];
    body.appendChild(script);
  }

  ngOnDestroy(): void {}
}
